import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { k8sResourceService, type K8sResource } from '@/services/k8sResourceService'
import { HttpError } from '@/lib/httpError'
import CardRefreshButton from '@/components/ui/CardRefreshButton'
import { licensePhaseLabel, licenseTypeLabel } from '@/lib/statusLabels'

export default function LicenseCard() {
  const { t } = useTranslation(['home', 'common'])
  const { refreshAuth } = useAuth()
  const [license, setLicense] = useState<K8sResource | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchFirstLicense()
  }, [])

  // Helper functions
  const isLicenseValid = (lic: K8sResource) => {
    // Trial and Exceeded are valid too; phase is a label, isValid is the verdict.
    return lic.status?.isValid === true
  }

  const isLicenseExpiringSoon = (lic: K8sResource, days: number = 30) => {
    const daysUntilExpiration = lic.status?.daysUntilExpiration || 0
    return daysUntilExpiration <= days && daysUntilExpiration > 0
  }

  const formatExpiryDate = (expiresAt: string) => {
    try {
      return new Date(expiresAt).toLocaleDateString()
    } catch {
      return 'Invalid Date'
    }
  }

  const getUsagePercentage = (lic: K8sResource) => {
    const currentFlowCount = lic.status?.currentFlowCount || 0
    const maxFlows = lic.status?.maxFlows || 1
    return Math.round((currentFlowCount / maxFlows) * 100)
  }

  const fetchFirstLicense = async () => {
    try {
      setLoading(true)
      const response = await k8sResourceService.getLicenses()
      const licenses = response.items
      const activeLicense = licenses.find(l => isLicenseValid(l))
      setLicense(activeLicense || licenses[0] || null)
      setError(null)
    } catch (err) {
      // Handle 401 errors with automatic token refresh
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry the fetch after token refresh
          return fetchFirstLicense()
        } catch (refreshError) {
          // If refresh fails, show error
          const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : 'errors.sessionExpired'
          if (refreshErrorMessage.startsWith('errors.')) {
            setError(t(refreshErrorMessage, { ns: 'common', defaultValue: refreshErrorMessage }))
          } else {
            setError(refreshErrorMessage)
          }
          setLicense(null)
          setLoading(false)
          return
        }
      }

      // For other errors, show error message
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
      } else {
        setError(errorMessage)
      }
      setLicense(null)
    } finally {
      setLoading(false)
    }
  }

  const getStatusInfo = () => {
    if (!license) return { status: t('plan.noPlan'), color: 'text-gray-500', icon: 'inactive' }
    const phase = licensePhaseLabel(license.status?.phase)
    if (!isLicenseValid(license)) return { status: phase, color: 'text-red-600', icon: 'error' }
    if (isLicenseExpiringSoon(license)) return { status: phase, color: 'text-orange-500', icon: 'warning' }
    return { status: phase, color: 'text-green-600', icon: 'active' }
  }

  const statusInfo = getStatusInfo()

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 transition-all duration-300">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-200 rounded-lg mr-4 transition-all duration-300"></div>
              <div className="h-6 bg-gray-200 rounded w-32 transition-all duration-300"></div>
            </div>
            <div className="w-8 h-8 bg-gray-200 rounded-full transition-all duration-300"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded mb-4 transition-all duration-300"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg transition-all duration-300 ease-in-out" style={{ animationDelay: `${i * 100}ms` }}></div>
            ))}
          </div>
          <div className="mt-6 space-y-2">
            <div className="h-10 bg-gray-100 rounded-lg transition-all duration-300"></div>
            <div className="h-8 bg-gray-50 rounded transition-all duration-300"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mr-4">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-text-dark">{t('plan.title')}</h3>
        </div>
        <CardRefreshButton
          onRefresh={fetchFirstLicense}
          loading={loading}
        />
      </div>

      {error ? (
        <div className="text-center py-8 opacity-0 animate-fade-in" style={{ animation: 'fadeIn 0.5s ease-in-out forwards' }}>
          <div className="text-red-600 mb-4 transform transition-all duration-500 ease-in-out">
            <svg className="w-12 h-12 mx-auto transition-all duration-300 hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4 transition-colors duration-300">{error}</p>
          <button
            onClick={fetchFirstLicense}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95"
          >
            {t('plan.retry')}
          </button>
        </div>
      ) : !license ? (
        <div className="text-center py-8 opacity-0" style={{ animation: 'fadeIn 0.5s ease-in-out forwards' }}>
          <div className="text-gray-400 mb-4 transform transition-all duration-500 ease-in-out">
            <svg className="w-12 h-12 mx-auto transition-all duration-300 hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4 transition-colors duration-300">{t('plan.noPlan')}</p>
          <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95">
            {t('plan.getStarted')}
          </button>
        </div>
      ) : (
        <div className="opacity-0" style={{ animation: 'fadeIn 0.6s ease-in-out forwards' }}>
          <p className="text-gray-600 mb-4 transition-colors duration-300">
            {license.metadata.name} - {licenseTypeLabel(license.status?.licenseType)}
          </p>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-background-light rounded-lg border border-gray-100 hover:border-primary-200 transition-all duration-300 ease-in-out hover:shadow-sm transform hover:translate-x-1">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-3 shadow-sm">
                  {statusInfo.icon === 'active' && (
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {statusInfo.icon === 'warning' && (
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {statusInfo.icon === 'error' && (
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  {statusInfo.icon === 'inactive' && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-700">{t('plan.items.status')}</span>
              </div>
              <span className={`font-semibold ${statusInfo.color}`}>
                {statusInfo.status}
              </span>
            </div>

            <div className="flex justify-between items-center p-4 bg-background-light rounded-lg border border-gray-100 hover:border-primary-200 transition-all duration-300 ease-in-out hover:shadow-sm transform hover:translate-x-1" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-3 shadow-sm transition-all duration-300 hover:shadow-md">
                  <svg className="w-4 h-4 text-gray-600 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700 transition-colors duration-300">{t('plan.items.expiryDate')}</span>
              </div>
              <span className={`font-semibold transition-all duration-300 ${
                isLicenseExpiringSoon(license) ? 'text-orange-500' : 'text-text-dark'
              }`}>
                {formatExpiryDate(license.status?.expiresAt || '')}
              </span>
            </div>

            <div className="flex justify-between items-center p-4 bg-background-light rounded-lg border border-gray-100 hover:border-primary-200 transition-all duration-300 ease-in-out hover:shadow-sm transform hover:translate-x-1" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-3 shadow-sm">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">{t('plan.items.usage')}</span>
              </div>
              <span className={`font-semibold ${
                getUsagePercentage(license) >= 80 ? 'text-orange-500' : 'text-text-dark'
              }`}>
                {license.status?.currentFlowCount || 0} / {license.status?.maxFlows || 0}
              </span>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to="/plans"
              className="w-full text-secondary hover:text-secondary-600 font-medium text-sm transition-all duration-300 ease-in-out hover:bg-secondary-50 py-1 rounded block text-center"
            >
              {t('plan.detailsButton')}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}